/**
 * Shared constants/helpers for the Hub content library — used by HubLayout (the tree),
 * HubCategoryOverview (category landing), and HubDocumentPage (reader), so the category list
 * and status/outline logic live in exactly one place. Hub is read-only; editing happens in
 * the Security space's Document Register (DocumentDetail.tsx) against the same documents.
 */

export interface HubCategoryDef {
  key: string;
  // Absent only for the synthetic DPIA category (dpiaOnly: true) - every register-backed
  // category resolves this to a real register via documentApi.getRegisters().
  registerName?: string;
  label: string;
  shortLabel: string;
  icon: string;
  desc: string;
  // DPIAs are entity-linked (to an AI Use Case or Processing Activity), never register-linked,
  // so they can never satisfy the entityType='register' query every other category uses
  // (confirmed live: this is exactly why they never appeared in Hub before this category
  // existed). Fetched by documentType across the whole tenant instead of by register - the
  // one real behavioural difference every consumer of HUB_CATEGORIES needs to branch on.
  dpiaOnly?: boolean;
}

export const HUB_CATEGORIES: HubCategoryDef[] = [
  {
    key: 'policies',
    registerName: 'Information Security Policies',
    label: 'Information Security Policies',
    shortLabel: 'Policies',
    icon: 'ri-shield-check-line',
    desc: 'Group-level policies approved by leadership — access, data, people, and platform security.',
  },
  {
    key: 'processes',
    registerName: 'ISMS Processes',
    label: 'ISMS Processes',
    shortLabel: 'Processes',
    icon: 'ri-flow-chart',
    desc: 'The operating procedures that put each policy into practice day to day.',
  },
  {
    key: 'customer-assurance',
    registerName: 'Customer Assurance',
    label: 'Customer Assurance',
    shortLabel: 'Customer Assurance',
    icon: 'ri-customer-service-2-line',
    desc: 'Guidance and evidence for answering customer security questionnaires and portal requests.',
  },
  {
    key: 'training',
    registerName: 'Security Training & Awareness',
    label: 'Security Training & Awareness',
    shortLabel: 'Training & Awareness',
    icon: 'ri-graduation-cap-line',
    desc: 'Awareness campaigns, phishing drills, and onboarding material for every employee.',
  },
  {
    key: 'dpias',
    label: 'Data Protection Impact Assessments',
    shortLabel: 'DPIAs',
    icon: 'ri-shield-user-line',
    desc: 'Privacy impact assessments drafted for individual AI use cases and processing activities.',
    dpiaOnly: true,
  },
];

export type HubStatus = 'published' | 'draft' | 'stale';

/** A document is "stale" (review due) once its own review date has passed — the same
 * automatic, review-date-driven status Document360 uses, rather than a separate field
 * someone has to remember to update. */
export function effectiveStatus(doc: { status?: string; nextReviewDate?: string }): HubStatus {
  if (doc.status === 'draft') return 'draft';
  if (doc.nextReviewDate && new Date(doc.nextReviewDate).getTime() < Date.now()) return 'stale';
  return 'published';
}

export const STATUS_META: Record<HubStatus, { label: string; dotClass: string; tone: string; icon: string }> = {
  published: { label: 'Published', dotClass: 'hub-dot-published', tone: 'np-tone-success', icon: 'ri-checkbox-circle-line' },
  stale: { label: 'Review due', dotClass: 'hub-dot-stale', tone: 'np-tone-warning', icon: 'ri-error-warning-line' },
  draft: { label: 'Draft', dotClass: 'hub-dot-draft', tone: 'np-tone-neutral', icon: 'ri-draft-line' },
};

export interface DocTreeNode<T extends { documentId: string; parentDocumentId?: string }> {
  doc: T;
  children: DocTreeNode<T>[];
}

/** Builds a real tree from parentDocumentId links (set by dragging one document onto another
 * in HubLayout's tree) - shared so the sidebar tree and the category overview page nest
 * documents identically. Replaces an earlier mechanism (outlineLevel, parsing domainArea as a
 * bare digit for outline depth) that never actually grouped anything: domainArea is a free-text
 * "e.g., Information Security" field in the Properties tab that writes it, which never produces
 * a parseable integer for real content, so every document silently came out at level 0.
 *
 * A parent that points outside this document set (a different register, or a stale/deleted id)
 * falls back to top-level rather than vanishing, the same don't-lose-it instinct as every other
 * missing-field fallback added this session.
 *
 * The backend rejects a reparent that would create a cycle (would_create_cycle in
 * lambda_function.py), but that check is a plain read-then-write with no cross-document
 * transaction - two concurrent drags (A onto B, B onto A) can each individually pass it before
 * either write lands, committing a real A<->B cycle. Without the isCycle guard below, neither
 * A nor B would ever be reachable from `roots` (each finds the other as a "valid" parent) and
 * both would silently vanish from every tree view with no error and no way to select them again
 * to fix it. Detecting that here instead falls the cyclic pair back to top-level - visible,
 * recoverable by just dragging them again - rather than corrupted data becoming corrupted UI. */
export function buildDocTree<T extends { documentId: string; parentDocumentId?: string }>(docs: T[]): DocTreeNode<T>[] {
  const nodeById = new Map<string, DocTreeNode<T>>(docs.map((doc) => [doc.documentId, { doc, children: [] }]));
  const roots: DocTreeNode<T>[] = [];
  for (const node of nodeById.values()) {
    const parentId = node.doc.parentDocumentId;
    const parentNode = parentId ? nodeById.get(parentId) : undefined;
    const isCycle = !!parentId && isAncestorChainCyclic(node.doc.documentId, parentId, nodeById);
    if (parentNode && parentNode !== node && !isCycle) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/** Walks parentId's own chain of parentDocumentId links (via the raw doc data, not the
 * children arrays still being built) looking for startId - if found, startId is already its
 * own ancestor and attaching it under parentId would create or perpetuate a cycle. A visited
 * set means getting stuck circling an unrelated, already-corrupted pair elsewhere in the data
 * is recognised as "not my cycle" rather than misread as finding one back to startId. */
function isAncestorChainCyclic<T extends { documentId: string; parentDocumentId?: string }>(
  startId: string,
  parentId: string,
  nodeById: Map<string, DocTreeNode<T>>,
): boolean {
  let currentId: string | undefined = parentId;
  const visited = new Set<string>();
  while (currentId) {
    if (currentId === startId) return true;
    if (visited.has(currentId)) return false;
    visited.add(currentId);
    currentId = nodeById.get(currentId)?.doc.parentDocumentId;
  }
  return false;
}

/** Flattens a tree back into row order (depth-first, each node right after its parent) with a
 * depth number for indentation - what a plain list-based layout (HubCategoryOverview's rows,
 * unlike HubLayout's recursively-rendered tree) needs instead of the tree shape itself. Nodes
 * under a not-expanded id are omitted entirely, not just visually hidden.
 *
 * Takes a predicate, not a plain lookup object, specifically so subordinates default to
 * collapsed: a caller tracking "which ids has the user explicitly expanded" (starting empty)
 * passes `(id) => !!expandedIds[id]`, which is false - collapsed - for every id until it's
 * actually clicked open, with no need to already know every master id up front to seed a
 * default-true map for each of them. */
export function flattenDocTree<T extends { documentId: string; parentDocumentId?: string }>(
  nodes: DocTreeNode<T>[],
  isExpanded: (documentId: string) => boolean,
  depth = 0,
): { doc: T; depth: number; hasChildren: boolean }[] {
  const rows: { doc: T; depth: number; hasChildren: boolean }[] = [];
  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    rows.push({ doc: node.doc, depth, hasChildren });
    if (hasChildren && isExpanded(node.doc.documentId)) {
      rows.push(...flattenDocTree(node.children, isExpanded, depth + 1));
    }
  }
  return rows;
}

/** The 76 documents migrated from the source PDF export each open with their cover page's
 * metadata restated as plain paragraphs — Title/Document Type/Department of Ownership/
 * Approver/Primary Audience/Data Classification — which the meta-rail already shows properly.
 * Left in, it reads as a raw data dump before the actual document starts. Stripped only for
 * display (the stored content, and the editor, are untouched) so it's harmless on documents
 * that never had it, e.g. anything created directly in Hub. */
const LEADING_METADATA_RE = /^(?:\s*<p><strong>[^<]+:<\/strong>[^<]*<\/p>)+/;
export function stripMetadataPreamble(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(LEADING_METADATA_RE, '');
}

/** Turns heading text into a stable, URL-safe anchor id. Deliberately keyed on the heading's
 * own text rather than its position in the document (the previous `hub-sec-${id}-${i}` scheme)
 * - a positional id shifts every time a section is added/removed/reordered above it, breaking
 * every link anyone had already shared or bookmarked to that heading. */
export function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  return slug || 'section';
}

export function formatUpdated(updatedAt: number | string | undefined): string {
  if (!updatedAt) return '—';
  const ms = typeof updatedAt === 'number' ? updatedAt * 1000 : Date.parse(updatedAt);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export interface DocHeading {
  id: string;
  text: string;
  level: number;
  node: Element;
}

const HEADING_LEVEL_BY_TAG: Record<string, number> = { H2: 0, H3: 1, H4: 2, H5: 3 };

/** Extracts h2-h5 headings from any DOM root - a live element ref (HubDocumentPage's own
 * currently-open document) or a detached document parsed via DOMParser (a category-overview
 * card previewing a document that isn't open) - into the same stable, slugified id every
 * caller needs to agree on (see slugifyHeading) so a link built from one matches the anchor id
 * the other assigns. h2 is included alongside h3-h5 because DocumentDetail's section-based
 * editor stores every top-level section title as an <h2> (assembleSectionsToHtml).
 *
 * Never mutates the source nodes itself - a detached DOMParser document has no live page to
 * affect anyway, and callers that need node.id actually set on a live, rendered document
 * (HubDocumentPage's own scroll-spied ToC) do that themselves with the returned node
 * reference, rather than this running the assignment unconditionally for a case that doesn't
 * want it. */
export function extractHeadings(root: ParentNode): DocHeading[] {
  const nodes = Array.from(root.querySelectorAll('h2, h3, h4, h5'));
  const seen = new Map<string, number>();
  return nodes.map((node) => {
    const text = node.textContent || '';
    const base = slugifyHeading(text);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;
    return { id, text, level: HEADING_LEVEL_BY_TAG[node.tagName] ?? 0, node };
  });
}

/** Same extraction, starting from a raw content HTML string rather than an already-mounted DOM
 * subtree - what a preview card needs, since it isn't rendering the other document's content
 * anywhere on the page to have a live root to query in the first place. */
export function extractHeadingsFromHtml(html: string): DocHeading[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return extractHeadings(doc);
}

const HTML_ENTITY_REPLACEMENTS: [RegExp, string][] = [
  [/&nbsp;/g, ' '], [/&amp;/g, '&'], [/&lt;/g, '<'], [/&gt;/g, '>'], [/&quot;/g, '"'], [/&#39;/g, "'"],
];

/** A short plain-text preview built from a light scan across the whole document, not just its
 * first paragraph verbatim - the metadata preamble the PDF-migrated documents open with
 * (stripMetadataPreamble) is cut first so it never IS the "first paragraph", and text is
 * allowed to flow across every paragraph/heading boundary rather than stopping wherever the
 * very first <p> happens to end, which is often just a one-line lead-in. Cut at the last whole
 * word inside the limit, not mid-word. */
export function extractSummary(html: string, maxLength = 200): string {
  const withoutPreamble = stripMetadataPreamble(html);
  let plain = withoutPreamble.replace(/<[^>]+>/g, ' ');
  for (const [pattern, replacement] of HTML_ENTITY_REPLACEMENTS) {
    plain = plain.replace(pattern, replacement);
  }
  plain = plain.replace(/\s+/g, ' ').trim();
  if (plain.length <= maxLength) return plain;
  const truncated = plain.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 40 ? truncated.slice(0, lastSpace) : truncated).trim() + '…';
}
